import { instantFromIso, nowInstant } from "@nijiurachan/js/util/time";
import noImage from "@/assets/img/no-image.svg";
import type { Attachment } from "@/entities/attachment";
import type { Post } from "@/entities/post";
import type { ThreadSummary, ThreadView } from "@/entities/thread/types";
import { UPLOADS_BASE } from "@/shared/api";
import { decodeHtmlEntities } from "@/shared/lib";

type ThreadWithOp = Pick<ThreadSummary, "opPost"> | Pick<ThreadView, "posts">;

export { isR18ThreadTagName } from "@/shared/lib/threadTag";

/** 非永久スレッドのexpiresAtが現在時刻より前なら、一覧表示上の期限切れとして扱う。 */
export function isThreadExpired(
  thread: Pick<ThreadSummary, "expiresAt" | "isPermanent">,
  now: Temporal.Instant = nowInstant(),
): boolean {
  if (thread.isPermanent) return false;
  const { expiresAt } = thread;
  if (!expiresAt) return false;
  const expiry = instantFromIso(expiresAt);
  return expiry !== null && expiry.epochMilliseconds < now.epochMilliseconds;
}

export function getThreadOp(thread: ThreadWithOp): Post | null {
  if ("opPost" in thread) return thread.opPost;
  return thread.posts[0] ?? null;
}

export function getThreadTitle(
  thread: ThreadWithOp,
  maxLength: number = 30,
): string {
  return getDecodedThreadBody(thread).slice(0, maxLength) || "(無題)";
}

/** カタログなどでCSSによる表示制限を行うための、切り詰め前のOP本文。 */
export function getThreadBodyText(thread: ThreadWithOp): string {
  return getDecodedThreadBody(thread) || "(無題)";
}

function getDecodedThreadBody(thread: ThreadWithOp): string {
  const body = getThreadOp(thread)?.body.trim() ?? "";
  return decodeHtmlEntities(body);
}

export function resolveUploadPath(path: string): string {
  if (!path) return noImage;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/uploads/")) {
    return `${UPLOADS_BASE}${path.slice("/uploads".length)}`;
  }
  if (path.startsWith("uploads/")) {
    return `${UPLOADS_BASE}/${path.slice("uploads/".length)}`;
  }
  return path.startsWith("/") ? path : `/${path}`;
}

export function getImageUrl(
  attachment: Attachment | null | undefined,
  preferOriginal: boolean,
): string {
  if (!attachment) return noImage;
  return resolveUploadPath(
    preferOriginal ? attachment.originalUrl : attachment.thumbnailUrl,
  );
}
